import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '@/api/client';
import { SortableTh, SortDir } from '@/components/SortableTh';
import { labelNivel, NIVEL_OPTIONS, TIPO_ELECCION_OPTIONS } from '@/lib/labels';

interface ListaRow {
  id: number;
  n_lista: number;
  region: string;
  provincia: string | null;
  distrito: string | null;
  nivel: string;
  tipo_eleccion: string;
  alerta_maxima: string;
  estado_jee: string;
  n_candidatos: number;
  n_designados: number;
  cabeza_cargo: string | null;
  cabeza_nombre: string | null;
  cabeza_dni: string | null;
  cabeza_es_designado: boolean;
  n_djhv_completa: number;
  n_djhv_confirmada: number;
  n_docs_aprobados_tot: number;
  n_docs_aplicables_tot: number;
  pct_docs: number;
  n_bloqueantes: number;
}

const CARGO_LABEL: Record<string, string> = {
  GOB: 'Gobernador',
  VGR: 'Vicegobernador',
  ALC: 'Alcalde',
  REG: 'Regidor',
  CR:  'Consejero',
};

const CARGO_PILL: Record<string, string> = {
  GOB: 'bg-purple-100 text-purple-800 border-purple-300',
  ALC: 'bg-indigo-100 text-indigo-800 border-indigo-300',
};

interface Filtros {
  regiones: string[];
  provincias_por_region: Record<string, string[]>;
  niveles: string[];
  tipos_eleccion: string[];
}

interface Paged {
  total: number;
  page: number;
  page_size: number;
  items: ListaRow[];
}

const ALERTA_PILL: Record<string, string> = {
  ok: 'bg-green-100 text-green-800',
  observacion: 'bg-amber-100 text-amber-800',
  bloqueante: 'bg-red-100 text-red-800',
};

export function Listas() {
  const [sp] = useSearchParams();
  const [region, setRegion] = useState(sp.get('region') ?? '');
  const [provincia, setProvincia] = useState(sp.get('provincia') ?? '');
  const [nivel, setNivel] = useState(sp.get('nivel') ?? '');
  const [tipoEleccion, setTipoEleccion] = useState(sp.get('tipo_eleccion') ?? '');
  const [alerta, setAlerta] = useState(sp.get('alerta_maxima') ?? '');
  const [estadoJee, setEstadoJee] = useState(sp.get('estado_jee') ?? '');
  const cumpleCuotaJoven = sp.get('cumple_cuota_joven') ?? '';
  const cumpleParidad = sp.get('cumple_paridad_horizontal') ?? '';
  const cumpleAlternancia = sp.get('cumple_alternancia') ?? '';
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>(null);
  // Filtros numéricos por columna (client-side sobre la página visible)
  const [filterCandRange, setFilterCandRange] = useState<{ min: number | ''; max: number | '' }>({ min: '', max: '' });
  const [filterDesigRange, setFilterDesigRange] = useState<{ min: number | ''; max: number | '' }>({ min: '', max: '' });
  const [filterBloqRange, setFilterBloqRange] = useState<{ min: number | ''; max: number | '' }>({ min: '', max: '' });
  const [filterPctRange, setFilterPctRange] = useState<{ min: number | ''; max: number | '' }>({ min: '', max: '' });
  const [page, setPage] = useState(1);
  const pageSize = 100;

  const onSort = (key: string, dir: SortDir) => {
    setSortBy(dir ? key : null);
    setSortDir(dir);
    setPage(1);
  };

  const { data: filtros } = useQuery<Filtros>({
    queryKey: ['avance-filtros'],
    queryFn: async () => (await api.get<Filtros>('/avance/filtros')).data,
    staleTime: 5 * 60 * 1000,
  });

  const { data, isLoading } = useQuery<Paged>({
    queryKey: ['listas', region, provincia, nivel, tipoEleccion, alerta, estadoJee, cumpleCuotaJoven, cumpleParidad, cumpleAlternancia, sortBy, sortDir, page],
    queryFn: async () => {
      const { data } = await api.get<Paged>('/listas', {
        params: {
          region: region || undefined,
          provincia: provincia || undefined,
          nivel: nivel || undefined,
          tipo_eleccion: tipoEleccion || undefined,
          alerta_maxima: alerta || undefined,
          estado_jee: estadoJee || undefined,
          cumple_cuota_joven: cumpleCuotaJoven === '' ? undefined : cumpleCuotaJoven === 'true',
          cumple_paridad_horizontal: cumpleParidad === '' ? undefined : cumpleParidad === 'true',
          cumple_alternancia: cumpleAlternancia === '' ? undefined : cumpleAlternancia === 'true',
          sort_by: sortBy || undefined,
          sort_dir: sortBy ? sortDir : undefined,
          page,
          page_size: pageSize,
        },
      });
      return data;
    },
  });

  // Filtros numéricos client-side
  const filtered = (data?.items ?? []).filter((l) => {
    const inRange = (v: number, r: { min: number | ''; max: number | '' }) =>
      (r.min === '' || v >= (r.min as number)) && (r.max === '' || v <= (r.max as number));
    if (!inRange(l.n_candidatos, filterCandRange)) return false;
    if (!inRange(l.n_designados, filterDesigRange)) return false;
    if (!inRange(l.n_bloqueantes, filterBloqRange)) return false;
    if (!inRange(l.pct_docs, filterPctRange)) return false;
    return true;
  });

  const provincias = region ? (filtros?.provincias_por_region?.[region] ?? []) : [];

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <div className="p-6 space-y-4 max-w-7xl">
      <header>
        <h1 className="text-xl font-bold text-arpp-primary">Listas</h1>
        <p className="text-xs text-gray-600">
          {data?.total ?? '…'} listas en el sistema · 1 lista por cada Región / Municipalidad Provincial / Municipalidad Distrital donde participa ARPP
        </p>
        <p className="text-[11px] text-gray-500 italic mt-0.5">
          La columna <strong>#prim.</strong> es el número de lista interna de primarias (1 o 2) — referencial. La inscripción ante JEE se hace con una sola lista por circunscripción.
        </p>
      </header>

      <div className="flex flex-wrap gap-3 items-end bg-white border rounded p-3">
        <div>
          <label className="block text-[11px] text-gray-600">Región</label>
          <select value={region} onChange={(e) => { setPage(1); setRegion(e.target.value); setProvincia(''); }}
            className="border rounded px-2 py-1 text-sm w-44">
            <option value="">Todas</option>
            {filtros?.regiones.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[11px] text-gray-600">Provincia</label>
          <select value={provincia} onChange={(e) => { setPage(1); setProvincia(e.target.value); }}
            disabled={!region}
            className="border rounded px-2 py-1 text-sm w-44 disabled:bg-gray-50">
            <option value="">{region ? 'Todas' : 'Selecciona región'}</option>
            {provincias.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[11px] text-gray-600">Tipo de elección</label>
          <select value={tipoEleccion} onChange={(e) => { setPage(1); setTipoEleccion(e.target.value); }}
            className="border rounded px-2 py-1 text-sm w-40">
            <option value="">Todas</option>
            {TIPO_ELECCION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[11px] text-gray-600">Nivel</label>
          <select value={nivel} onChange={(e) => { setPage(1); setNivel(e.target.value); }}
            className="border rounded px-2 py-1 text-sm w-52">
            <option value="">Todos</option>
            {NIVEL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[11px] text-gray-600">Alerta</label>
          <select value={alerta} onChange={(e) => { setPage(1); setAlerta(e.target.value); }}
            className="border rounded px-2 py-1 text-sm">
            <option value="">Todas</option>
            <option value="ok">🟢 OK</option>
            <option value="observacion">⚠ Observación</option>
            <option value="bloqueante">🔴 Bloqueante</option>
          </select>
        </div>
      </div>

      <div className="bg-white border rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs">
            <tr>
              <SortableTh label="#prim." sortKey="n_lista" sortBy={sortBy} sortDir={sortDir} onSort={onSort}
                className="text-gray-500" />
              <SortableTh label="Región" sortKey="region" sortBy={sortBy} sortDir={sortDir} onSort={onSort}
                filterOptions={filtros?.regiones ?? []} filterValue={region} onFilter={(v) => { setRegion(v); setProvincia(''); setPage(1); }} />
              <SortableTh label="Provincia" sortKey="provincia" sortBy={sortBy} sortDir={sortDir} onSort={onSort}
                filterOptions={region ? (filtros?.provincias_por_region?.[region] ?? []) : []} filterValue={provincia} onFilter={(v) => { setProvincia(v); setPage(1); }} />
              <SortableTh label="Distrito" sortKey="distrito" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <SortableTh label="Nivel" sortKey="tipo_eleccion" sortBy={sortBy} sortDir={sortDir} onSort={onSort}
                filterOptions={NIVEL_OPTIONS} filterValue={nivel} onFilter={(v) => { setNivel(v); setPage(1); }} />
              <th className="px-2 py-2 text-left">Cargo postula</th>
              <SortableTh label="Cand" align="right" filterType="numeric"
                filterMin={filterCandRange.min} filterMax={filterCandRange.max}
                onFilterRange={(mn, mx) => setFilterCandRange({ min: mn, max: mx })} />
              <SortableTh label="Desig" align="right" filterType="numeric"
                filterMin={filterDesigRange.min} filterMax={filterDesigRange.max}
                onFilterRange={(mn, mx) => setFilterDesigRange({ min: mn, max: mx })} />
              <SortableTh label="🔴" align="right" filterType="numeric"
                filterMin={filterBloqRange.min} filterMax={filterBloqRange.max}
                onFilterRange={(mn, mx) => setFilterBloqRange({ min: mn, max: mx })} />
              <th className="px-2 py-2 text-right">DJHV</th>
              <th className="px-2 py-2 text-right">Docs</th>
              <SortableTh label="% Docs" align="right" filterType="numeric"
                filterMin={filterPctRange.min} filterMax={filterPctRange.max}
                onFilterRange={(mn, mx) => setFilterPctRange({ min: mn, max: mx })} />
              <SortableTh label="Alerta" sortKey="alerta_maxima" sortBy={sortBy} sortDir={sortDir} onSort={onSort}
                filterOptions={['ok', 'observacion', 'bloqueante']} filterValue={alerta} onFilter={(v) => { setAlerta(v); setPage(1); }} />
              <SortableTh label="Estado JEE" sortKey="estado_jee" sortBy={sortBy} sortDir={sortDir} onSort={onSort}
                filterOptions={['no_presentado', 'presentado', 'calificando', 'con_observacion', 'admitido', 'tachado', 'apelado', 'inscrito']}
                filterValue={estadoJee} onFilter={(v) => { setEstadoJee(v); setPage(1); }} />
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td className="p-4 text-gray-500" colSpan={14}>Cargando…</td></tr>
            )}
            {filtered.map((l) => (
              <tr key={l.id} className="border-t hover:bg-gray-50">
                <td className="px-2 py-1.5">
                  <Link to={`/listas/${l.id}`} className="text-gray-600 hover:text-arpp-primary text-xs" title="Número de lista interna de primarias (referencial)">
                    {l.n_lista}
                  </Link>
                </td>
                <td className="px-2 py-1.5">{l.region}</td>
                <td className="px-2 py-1.5">{l.provincia ?? '—'}</td>
                <td className="px-2 py-1.5">{l.distrito ?? '—'}</td>
                <td className="px-2 py-1.5 text-xs">{labelNivel(l.tipo_eleccion)}</td>
                <td className="px-2 py-1.5 text-xs">
                  {l.cabeza_cargo ? (
                    <div className="flex flex-col gap-0.5">
                      <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded border ${CARGO_PILL[l.cabeza_cargo] || 'bg-gray-100 text-gray-700 border-gray-300'} self-start`}>
                        {l.cabeza_cargo} · {CARGO_LABEL[l.cabeza_cargo] ?? l.cabeza_cargo}
                      </span>
                      {l.cabeza_es_designado && !l.cabeza_nombre ? (
                        <span className="text-[10px] text-amber-700 italic">🟡 Designado pendiente</span>
                      ) : l.cabeza_nombre ? (
                        <span className="text-[10px] text-gray-700 truncate max-w-[180px]" title={l.cabeza_nombre}>
                          {l.cabeza_nombre} {l.cabeza_dni && <span className="font-mono opacity-70">· {l.cabeza_dni}</span>}
                        </span>
                      ) : null}
                    </div>
                  ) : (
                    <span className="text-gray-400 italic">—</span>
                  )}
                </td>
                <td className="px-2 py-1.5 text-right">{l.n_candidatos}</td>
                <td className="px-2 py-1.5 text-right text-amber-700">{l.n_designados}</td>
                <td className="px-2 py-1.5 text-right">
                  <span className={l.n_bloqueantes > 0 ? 'text-red-700 font-medium' : 'text-gray-400'}>
                    {l.n_bloqueantes}
                  </span>
                </td>
                <td className="px-2 py-1.5 text-right text-xs">
                  {l.n_djhv_completa}/{l.n_candidatos}
                  {l.n_djhv_confirmada > 0 && <span className="text-green-700"> ({l.n_djhv_confirmada}✓)</span>}
                </td>
                <td className="px-2 py-1.5 text-right text-xs">{l.n_docs_aprobados_tot}/{l.n_docs_aplicables_tot}</td>
                <td className="px-2 py-1.5 text-right text-xs">
                  <div className="flex items-center gap-1 justify-end">
                    <span>{l.pct_docs}%</span>
                    <div className="w-10 bg-gray-200 rounded h-1.5">
                      <div className={`h-1.5 rounded ${l.pct_docs >= 80 ? 'bg-green-600' : l.pct_docs >= 40 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${Math.min(100, l.pct_docs)}%` }} />
                    </div>
                  </div>
                </td>
                <td className="px-2 py-1.5">
                  <span className={`text-[11px] px-2 py-0.5 rounded ${ALERTA_PILL[l.alerta_maxima] || ''}`}>
                    {l.alerta_maxima}
                  </span>
                </td>
                <td className="px-2 py-1.5 text-xs">{l.estado_jee}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-3 text-xs">
        <button disabled={page <= 1} onClick={() => setPage(page - 1)}
          className="border rounded px-3 py-1 disabled:opacity-40">←</button>
        <span>Página {page} de {totalPages}</span>
        <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}
          className="border rounded px-3 py-1 disabled:opacity-40">→</button>
      </div>
    </div>
  );
}
